import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { setupModalDeeplink } from '@theme/modal-deeplink';

/**
 * @typedef {Object} BulkTier
 * @property {number} min_qty
 * @property {'percent'|'fixed_price'} discount_type
 * @property {number} value  - percent off, or per-unit price in major currency units (e.g. dollars)
 *
 * @typedef {Object} BulkSchedule
 * @property {string} id
 * @property {string} name
 * @property {string[]} applies_to_format_names
 * @property {BulkTier[]} tiers
 *
 * @typedef {Object} BulkVariant
 * @property {number} id
 * @property {string} title
 * @property {number} price
 * @property {string} format
 * @property {string[]} options
 * @property {boolean} available
 * @property {string} [override_inline]  - raw custom.bulk_inline_tiers string
 * @property {BulkTier[]|null} [override_tiers] - tiers from a custom.bulk_schedule override
 *
 * @typedef {Object} ResolvedTier
 * @property {number} min
 * @property {number|null} max
 * @property {number} price
 * @property {number} discountPct
 */

class BuyInBulkModal extends DialogComponent {
  /** @type {BulkSchedule[]} */ #schedules = [];
  /** @type {BulkVariant[]} */  #variants = [];
  /** @type {BulkVariant|null} */ #variant = null;
  /** @type {ResolvedTier[]} */ #tiers = [];
  /** @type {string[]} */ #selectedOptions = [];
  /** @type {HTMLSelectElement[]} */ #optionSelects = [];

  #formatOptionIndex = -1;
  #moneyFormat = '{{amount}}';
  #currency = 'USD';
  #qty = 1;
  #activeTierIndex = 0;
  #isSubmitting = false;
  #blockId = '';

  #trigger = null;
  #cleanupDeeplink = null;
  #section = null;
  #bodyEl = null;
  #footerEl = null;
  #resizeObserver = null;
  #formatsEl = null;
  #tiersEl = null;
  #qtyInput = null;
  #subtotalEl = null;
  #subtotalCompareEl = null;
  #subtotalDiscountEl = null;
  #addLabelEl = null;
  #addButton = null;
  #liveRegion = null;

  #addLabelTemplates = { one: 'Add {count} to Cart', other: 'Add {count} to Cart' };
  #addLabelShortTemplates = { one: 'Add {count}', other: 'Add {count}' };
  #savePctTemplate = 'Save {pct}%';
  #bestValueLabel = 'Best Value';
  #addingLabel = 'Adding…';
  #eachLabel = '/ea';

  // On mobile the add-to-cart button shares one line with the subtotal +
  // stepper, so it uses the shorter "Add {count}" label to fit.
  #mobileQuery = window.matchMedia('(max-width: 749px)');

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#schedules = JSON.parse(this.dataset.schedules || '[]');
    } catch {
      this.#schedules = [];
    }
    try {
      this.#variants = JSON.parse(this.dataset.variants || '[]');
    } catch {
      this.#variants = [];
    }

    this.#formatOptionIndex = Number(this.dataset.formatOptionIndex);
    if (!Number.isFinite(this.#formatOptionIndex)) this.#formatOptionIndex = -1;
    this.#blockId = this.dataset.blockId || '';

    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.#currency = this.dataset.currency || 'USD';
    this.#addLabelTemplates = {
      one: this.dataset.addLabelOne || 'Add {count} to Cart',
      other: this.dataset.addLabelOther || 'Add {count} to Cart',
    };
    this.#addLabelShortTemplates = {
      one: this.dataset.addLabelShortOne || 'Add {count}',
      other: this.dataset.addLabelShortOther || 'Add {count}',
    };
    this.#savePctTemplate = this.dataset.saveLabel || 'Save {pct}%';
    this.#bestValueLabel = this.dataset.bestValueLabel || 'Best Value';
    this.#addingLabel = this.dataset.addingLabel || 'Adding…';
    this.#eachLabel = this.dataset.eachLabel || '/ea';

    this.#trigger = this.querySelector('[data-buy-in-bulk-trigger]');
    this.#bodyEl = this.querySelector('.buy-in-bulk-modal__body');
    this.#footerEl = this.querySelector('.buy-in-bulk-modal__footer');
    this.#formatsEl = this.querySelector('[data-formats]');
    this.#tiersEl = this.querySelector('[data-tiers]');
    this.#qtyInput = this.querySelector('[data-qty-input]');
    this.#subtotalEl = this.querySelector('[data-subtotal]');
    this.#subtotalCompareEl = this.querySelector('[data-subtotal-compare]');
    this.#subtotalDiscountEl = this.querySelector('[data-subtotal-discount]');
    this.#addLabelEl = this.querySelector('[data-add-label]');
    this.#addButton = this.querySelector('[data-add-button]');
    this.#liveRegion = this.querySelector('[data-live-region]');

    const initialId = Number(this.dataset.initialVariantId) || 0;
    this.#variant =
      this.#variants.find((v) => v.id === initialId) ?? this.#variants[0] ?? null;
    this.#selectedOptions = this.#variant ? [...this.#variant.options] : [];
    this.#optionSelects = /** @type {HTMLSelectElement[]} */ ([
      ...this.querySelectorAll('[data-option-select]'),
    ]);

    this.#resolveTiers();
    this.#renderTiers();

    this.#qty = this.#tiers[0]?.min ?? 1;
    if (this.#qtyInput) this.#qtyInput.value = String(this.#qty);

    this.#syncFromQty();
    // Initial pass: hide formats that don't exist for the starting language and
    // annotate out-of-stock states.
    this.#updateVariantStates();

    this.#trigger?.addEventListener('click', this.#handleTriggerClick);
    this.#formatsEl?.addEventListener('change', this.#handleFormatChange);
    for (const select of this.#optionSelects) {
      select.addEventListener('change', this.#handleOptionChange);
    }
    this.#tiersEl?.addEventListener('change', this.#handleTierChange);
    this.querySelector('[data-qty-decrement]')?.addEventListener('click', this.#handleDecrement);
    this.querySelector('[data-qty-increment]')?.addEventListener('click', this.#handleIncrement);
    this.#qtyInput?.addEventListener('input', this.#handleQtyInput);
    this.#qtyInput?.addEventListener('focus', this.#handleQtyFocus);
    this.#qtyInput?.addEventListener('blur', this.#handleQtyBlur);
    this.#addButton?.addEventListener('click', this.#handleAdd);
    this.addEventListener('bottom-drawer:close', this.closeDialog);
    this.#mobileQuery.addEventListener('change', this.#handleViewportChange);
    // Recompute the footer divider whenever the body box changes size (open,
    // viewport resize). Content-driven height changes (tier re-render) call
    // #updateFooterDivider explicitly.
    if (this.#bodyEl && 'ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#updateFooterDivider);
      this.#resizeObserver.observe(this.#bodyEl);
    }
    this.#cleanupDeeplink = setupModalDeeplink(this, {
      id: this.dataset.modalId,
      open: this.#open,
    });

    // Keep the modal in step with the product page: mirror the PDP's variant
    // and quantity selections (the modal is display-only, so the page stays the
    // source of truth). Variant/quantity events bubble to the product section.
    this.#section = this.closest('.shopify-section');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handlePageVariantUpdate);
    this.#section?.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handlePageQtyUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#trigger?.removeEventListener('click', this.#handleTriggerClick);
    this.#formatsEl?.removeEventListener('change', this.#handleFormatChange);
    for (const select of this.#optionSelects) {
      select.removeEventListener('change', this.#handleOptionChange);
    }
    this.#tiersEl?.removeEventListener('change', this.#handleTierChange);
    this.querySelector('[data-qty-decrement]')?.removeEventListener('click', this.#handleDecrement);
    this.querySelector('[data-qty-increment]')?.removeEventListener('click', this.#handleIncrement);
    this.#qtyInput?.removeEventListener('input', this.#handleQtyInput);
    this.#qtyInput?.removeEventListener('focus', this.#handleQtyFocus);
    this.#qtyInput?.removeEventListener('blur', this.#handleQtyBlur);
    this.#detachKeyboardInset();
    this.#addButton?.removeEventListener('click', this.#handleAdd);
    this.removeEventListener('bottom-drawer:close', this.closeDialog);
    this.#mobileQuery.removeEventListener('change', this.#handleViewportChange);
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handlePageVariantUpdate);
    this.#section?.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#handlePageQtyUpdate
    );
    this.#resizeObserver?.disconnect();
    this.#cleanupDeeplink?.();
  }

  // ----- Resolution -----

  /**
   * Schedules from the shop mirror that cover the current variant's format and
   * carry tiers. Multiple schedules can overlap one format (e.g. a global
   * schedule plus a product-specific override). The Creatine Function applies
   * whichever yields the greatest discount, so the modal must consider them all
   * rather than the first by mirror order — see #resolveTiers.
   */
  #applicableSchedules() {
    if (!this.#variant) return [];
    const format = this.#variant.format;
    return this.#schedules.filter(
      (s) => s.applies_to_format_names?.includes(format) && s.tiers?.length
    );
  }

  /** Per-unit price (cents) a single tier charges, clamped to list price. */
  #tierPrice(tier, basePrice) {
    if (tier.discount_type === 'fixed_price') {
      // Mirror stores fixed prices in major units; the modal works in cents.
      // Clamp to base so a fixed price above list never shows a surcharge.
      const fixedPrice = Math.round((Number(tier.value) || 0) * 100);
      return Math.min(fixedPrice, basePrice);
    }
    const pct = Number(tier.value) || 0;
    return Math.round(basePrice * (1 - pct / 100));
  }

  /**
   * Resolve the displayed tier ladder for the current variant, mirroring the
   * Creatine Function's priority chain so the modal matches checkout:
   *   1. inline tiers (custom.bulk_inline_tiers) — strict parse; malformed is
   *      an explicit skip (no discount), same as the Function.
   *   2. specific-schedule override (custom.bulk_schedule) — its tiers win
   *      outright over targeting.
   *   3. targeting schedules — greatest-discount-wins across all that cover
   *      this format.
   * An override (1 or 2) wins outright; only when none applies do we fall
   * through to the targeting schedules.
   */
  #resolveTiers() {
    const basePrice = this.#variant?.price ?? 0;
    const override = this.#variantOverride();
    if (override !== undefined) {
      // null = malformed inline tiers → explicit skip (base price only).
      this.#tiers = this.#buildTierLadder(override === null ? [] : [override], basePrice);
      return;
    }
    const schedules = this.#applicableSchedules();
    this.#tiers = this.#buildTierLadder(schedules.map((s) => s.tiers), basePrice);
  }

  /**
   * Per-variant override tier set for the current variant, mirroring the
   * Function's priority 1 & 2:
   *   undefined → no override (fall through to targeting)
   *   null      → malformed inline tiers (explicit skip — no discount)
   *   BulkTier[] → the override tier set to display
   */
  #variantOverride() {
    const v = this.#variant;
    if (!v) return undefined;
    if (typeof v.override_inline === 'string' && v.override_inline.trim() !== '') {
      return parseInlineTiers(v.override_inline);
    }
    if (Array.isArray(v.override_tiers) && v.override_tiers.length) {
      return v.override_tiers;
    }
    return undefined;
  }

  /**
   * Build the displayed tier ladder from one or more tier sets. When several
   * sets apply (overlapping targeting schedules) we mirror the Function's
   * greatest-discount-wins: at each quantity breakpoint we charge the lowest
   * per-unit price any set offers, then collapse neighbouring rows that resolve
   * to the same price. The base row at qty 1 falls out naturally (no tier
   * discounts a single unit). An empty set list yields the base-price-only row.
   * @param {BulkTier[][]} tierSets
   * @param {number} basePrice
   * @returns {ResolvedTier[]}
   */
  #buildTierLadder(tierSets, basePrice) {
    const sets = tierSets.filter((t) => Array.isArray(t) && t.length);
    if (!sets.length) {
      return [{ min: 1, max: null, price: basePrice, discountPct: 0 }];
    }

    // Every quantity at which some set's pricing changes (plus the base).
    const breakpoints = new Set([1]);
    for (const tiers of sets) {
      for (const tier of tiers) {
        const q = Number(tier.min_qty);
        if (Number.isFinite(q) && q >= 1) breakpoints.add(q);
      }
    }
    const sorted = [...breakpoints].sort((a, b) => a - b);

    // Best (lowest) per-unit price across all sets at a given quantity.
    const bestPriceAt = (qty) => {
      let best = basePrice;
      for (const tiers of sets) {
        let applicable = null;
        for (const tier of tiers) {
          const q = Number(tier.min_qty);
          if (q <= qty && (applicable == null || q > Number(applicable.min_qty))) {
            applicable = tier;
          }
        }
        if (applicable == null) continue;
        const price = this.#tierPrice(applicable, basePrice);
        if (price < best) best = price;
      }
      return best;
    };

    const tiers = [];
    for (let i = 0; i < sorted.length; i++) {
      const min = sorted[i];
      const max = i + 1 < sorted.length ? sorted[i + 1] - 1 : null;
      const price = bestPriceAt(min);
      const discountPct = basePrice > 0 ? Math.round((1 - price / basePrice) * 100) : 0;
      const prev = tiers[tiers.length - 1];
      if (prev && prev.price === price) {
        prev.max = max;
      } else {
        tiers.push({ min, max, price, discountPct });
      }
    }
    return tiers;
  }

  // ----- Rendering -----

  #renderTiers() {
    if (!this.#tiersEl) return;
    const tierCount = this.#tiers.length;
    const html = this.#tiers
      .map((tier, index) => {
        // Graduated emphasis: 0 (neutral first tier) → 1 (strongest, final
        // tier), fed to CSS via --tier-progress so the Save % badge fill
        // ramps on the accent for any number of tiers.
        const progress = tierCount === 1 ? 0 : index / (tierCount - 1);
        const isMax = index === tierCount - 1 && tier.discountPct > 0;
        const range = tier.max == null ? `${tier.min}+` : `${tier.min}–${tier.max}`;
        const priceText = formatMoney(tier.price, this.#moneyFormat, this.#currency);
        const saveBadge =
          tier.discountPct > 0
            ? `<span class="product-badges--small buy-in-bulk-modal__tier-badge" data-tier-badge>${escapeHtml(
                this.#savePctTemplate.replace('{pct}', String(Math.round(tier.discountPct)))
              )}</span>`
            : '';
        const bestValueBadge = isMax
          ? `<span class="product-badges--small buy-in-bulk-modal__tier-best">${escapeHtml(
              this.#bestValueLabel
            )}</span>`
          : '';
        return `
          <label class="variant-option__button-label buy-in-bulk-modal__tier${
            isMax ? ' buy-in-bulk-modal__tier--max' : ''
          }" style="--tier-progress: ${progress.toFixed(3)}" data-tier-card data-tier-index="${index}">
            <input
              type="radio"
              name="buy-in-bulk-tier-${escapeAttr(this.#blockId)}"
              value="${index}"
              data-tier-input
            >
            <span class="variant-option__button-label__pill" aria-hidden="true"></span>
            <span class="buy-in-bulk-modal__tier-range">${escapeHtml(range)}</span>
            <span class="buy-in-bulk-modal__tier-row">
              <span class="buy-in-bulk-modal__tier-price">
                <span class="buy-in-bulk-modal__tier-amount">${priceText}</span>
                <span class="buy-in-bulk-modal__tier-each">${escapeHtml(this.#eachLabel)}</span>
              </span>
              ${saveBadge}
            </span>
            ${bestValueBadge}
          </label>
        `;
      })
      .join('');
    this.#tiersEl.innerHTML = html;
  }

  // ----- State sync -----

  #tierIndexForQty(qty) {
    for (let i = this.#tiers.length - 1; i >= 0; i--) {
      if (qty >= this.#tiers[i].min) return i;
    }
    return 0;
  }

  /** Analytics state: last tier/qty already reported (null until first sync). */
  #trackedTierIndex = null;
  #trackedQty = null;
  #qtyTrackTimer = 0;

  #syncFromQty() {
    if (!Number.isFinite(this.#qty) || this.#qty < 1) this.#qty = 1;

    this.#activeTierIndex = this.#tierIndexForQty(this.#qty);
    this.#trackQtyAndTier();

    for (const input of this.querySelectorAll('[data-tier-input]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.checked = Number(input.value) === this.#activeTierIndex;
    }

    if (this.#qtyInput && this.#qtyInput.value !== String(this.#qty)) {
      this.#qtyInput.value = String(this.#qty);
    }

    this.#updateSubtotal();
    this.#updateAddButtonState();
  }

  // Publishes bulk_quantity_changed (debounced past the stepper burst) and
  // bulk_tier_reached (on climbing into a higher discount tier). The first
  // sync after connect/format-switch only seeds the baseline, so opening the
  // modal never fires phantom interactions.
  #trackQtyAndTier() {
    const prevTier = this.#trackedTierIndex;
    this.#trackedTierIndex = this.#activeTierIndex;
    if (prevTier !== null && this.#activeTierIndex > prevTier) {
      const tier = this.#tiers[this.#activeTierIndex];
      window.publishStorefrontEvent?.('bulk_tier_reached', {
        min_qty: tier?.min ?? null,
        discount_pct: tier?.discountPct != null ? Math.round(tier.discountPct) : null,
        quantity: this.#qty,
        placement: 'buy_in_bulk',
      });
    }

    if (this.#trackedQty === null) {
      this.#trackedQty = this.#qty;
      return;
    }
    window.clearTimeout(this.#qtyTrackTimer);
    this.#qtyTrackTimer = window.setTimeout(() => {
      if (this.#qty === this.#trackedQty) return;
      this.#trackedQty = this.#qty;
      window.publishStorefrontEvent?.('bulk_quantity_changed', {
        quantity: this.#qty,
        placement: 'buy_in_bulk',
      });
    }, 800);
  }

  #updateSubtotal() {
    const tier = this.#tiers[this.#activeTierIndex];
    if (!tier) return;
    if (this.#subtotalEl) {
      this.#subtotalEl.textContent = formatMoney(
        tier.price * this.#qty,
        this.#moneyFormat,
        this.#currency
      );
    }
    // Pre-discount total (list price × qty), shown struck-through next to the
    // discounted subtotal so the saving is visible. Hidden when there's no
    // bulk discount on the active tier.
    if (this.#subtotalCompareEl) {
      const basePrice = this.#variant?.price ?? tier.price;
      if (tier.price < basePrice) {
        this.#subtotalCompareEl.textContent = formatMoney(
          basePrice * this.#qty,
          this.#moneyFormat,
          this.#currency
        );
        this.#subtotalCompareEl.hidden = false;
      } else {
        this.#subtotalCompareEl.textContent = '';
        this.#subtotalCompareEl.hidden = true;
      }
    }
    if (this.#subtotalDiscountEl) {
      const pct = Math.round(tier.discountPct);
      if (pct > 0) {
        this.#subtotalDiscountEl.textContent = this.#savePctTemplate.replace('{pct}', String(pct));
        this.#subtotalDiscountEl.hidden = false;
      } else {
        this.#subtotalDiscountEl.textContent = '';
        this.#subtotalDiscountEl.hidden = true;
      }
    }
  }

  #updateAddLabel() {
    if (!this.#addLabelEl) return;
    const templates = this.#mobileQuery.matches
      ? this.#addLabelShortTemplates
      : this.#addLabelTemplates;
    const template = this.#qty === 1 ? templates.one : templates.other;
    this.#addLabelEl.textContent = template.replace('{count}', String(this.#qty));
  }

  /**
   * Reflect stock on the add-to-cart button: an unavailable variant can't be
   * added, so disable the button (theme styles `:disabled`) and swap the label
   * to the out-of-stock text. When available, restore the normal add label.
   */
  #updateAddButtonState() {
    const available = !!this.#variant?.available;
    this.#addButton?.toggleAttribute('disabled', !available);
    const oosLabel = this.dataset.oosLabel || '';
    if (!available && this.#addLabelEl && oosLabel) {
      this.#addLabelEl.textContent = oosLabel;
    } else {
      this.#updateAddLabel();
    }
  }

  #handleViewportChange = () => {
    this.#updateAddButtonState();
  };

  /** Show the footer's top divider only when the body actually overflows. */
  #updateFooterDivider = () => {
    if (!this.#bodyEl || !this.#footerEl) return;
    const scrollable = this.#bodyEl.scrollHeight > this.#bodyEl.clientHeight + 1;
    this.#footerEl.toggleAttribute('data-scrollable', scrollable);
  };

  // ----- Handlers -----

  #open = () => {
    // A fresh open must never inherit keyboard-lift state from a previous
    // focus cycle: a stale inline --keyboard-inset shrinks the drawer and
    // parks it at the top of the layout viewport, so the shopper sees only
    // the dimmed backdrop ("grays out but no popup").
    this.#detachKeyboardInset();

    // A dialog still [open] with .dialog-closing is a close that hasn't
    // landed: either the ~200ms close animation is mid-flight (the shopper
    // re-clicked the trigger fast) or the close's async tail died and the
    // finished slide-out (fill: forwards) is holding the dialog invisible
    // while its backdrop keeps painting — a dimmed page with no modal.
    // Either way showDialog() would dead-end on its `dialog.open` early
    // return, so finish (or force) the close first, then open cleanly.
    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLDialogElement && dialog.open && dialog.classList.contains('dialog-closing')) {
      this.#reopenWhenClosed(dialog);
      return;
    }

    // Adopt the product page's current variant + quantity before showing, so the
    // modal always opens reflecting what's selected on the page.
    this.#syncFromProductPage();
    this.showDialog();
    requestAnimationFrame(() => {
      const closeBtn = this.querySelector('[data-close-trigger]');
      if (closeBtn instanceof HTMLElement) closeBtn.focus({ preventScroll: true });
      this.#updateFooterDivider();
    });
  };

  #reopenPending = false;

  /**
   * Wait for an in-flight close to finish, then re-run #open. If the close
   * never completes (stuck [open].dialog-closing), force the dialog shut so
   * the reopen starts from a clean closed state.
   * @param {HTMLDialogElement} dialog
   */
  #reopenWhenClosed(dialog) {
    if (this.#reopenPending) return;
    this.#reopenPending = true;
    /** @type {ReturnType<typeof setTimeout>} */
    let timer;
    const finish = () => {
      clearTimeout(timer);
      dialog.removeEventListener('close', finish);
      this.#reopenPending = false;
      if (dialog.open) {
        dialog.classList.remove('dialog-closing');
        dialog.close();
      }
      requestAnimationFrame(() => this.#open());
    };
    // Outlasts the close animation (var(--animation-speed, 200ms)) with room
    // to spare; only the stuck case ever hits the timeout path.
    timer = setTimeout(finish, 600);
    dialog.addEventListener('close', finish, { once: true });
  }

  #handleTriggerClick = (event) => {
    event.preventDefault();
    this.#open();
  };

  #handleFormatChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.formatInput == null) return;
    const idx = this.#formatOptionIndex >= 0 ? this.#formatOptionIndex : 0;
    this.#selectedOptions[idx] = input.value;
    this.#applySelection(idx);
  };

  #handleOptionChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || select.dataset.optionSelect == null) return;
    const index = Number(select.dataset.optionIndex);
    if (!Number.isFinite(index)) return;
    this.#selectedOptions[index] = select.value;
    this.#applySelection(index);
  };

  /**
   * Resolve the variant for the current option selection, update derived
   * state, and re-sync the controls — the resolved variant can differ from the
   * raw selection when a chosen combination doesn't exist.
   * @param {number} [changedIndex] - Option index the customer just changed.
   */
  #applySelection(changedIndex) {
    const resolved = this.#resolveVariant(changedIndex);
    if (!resolved) return;
    this.#variant = resolved;
    this.#selectedOptions = [...resolved.options];
    this.#syncControls();
    this.#updateVariantStates();
    this.#resolveTiers();
    this.#renderTiers();
    if (this.#qty < (this.#tiers[0]?.min ?? 1)) this.#qty = this.#tiers[0].min;
    this.#syncFromQty();
    this.#updateFooterDivider();
  }

  // ----- Product-page sync (PDP → modal) -----

  /**
   * True if the modal supports this format — either a targeting schedule covers
   * it or some variant of the format carries a per-variant override.
   */
  #isFormatCovered(format) {
    if (this.#schedules.some((s) => s.applies_to_format_names?.includes(format))) return true;
    return this.#variants.some((v) => v.format === format && this.#variantHasOverride(v));
  }

  /** True if the variant carries an inline-tier or specific-schedule override. */
  #variantHasOverride(v) {
    return (
      (typeof v.override_inline === 'string' && v.override_inline.trim() !== '') ||
      (Array.isArray(v.override_tiers) && v.override_tiers.length > 0)
    );
  }

  /**
   * Switch the modal to a known variant by id, re-deriving tiers and controls —
   * the modal's counterpart to a PDP variant change. Ignores variants from
   * other products and formats the modal doesn't cover (those stay disabled, so
   * the modal keeps its covered default rather than landing on a dead format).
   */
  #selectVariantById(id) {
    const target = this.#variants.find((v) => v.id === Number(id));
    if (!target || target.id === this.#variant?.id) return;
    if (!this.#isFormatCovered(target.format)) return;
    this.#variant = target;
    this.#selectedOptions = [...target.options];
    this.#syncControls();
    this.#updateVariantStates();
    this.#resolveTiers();
    this.#renderTiers();
    if (this.#qty < (this.#tiers[0]?.min ?? 1)) this.#qty = this.#tiers[0].min;
    this.#syncFromQty();
    this.#updateFooterDivider();
  }

  /** Adopt an external quantity (e.g. the PDP stepper) into the modal. */
  #syncQtyFromExternal(qty) {
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n < 1 || n === this.#qty) return;
    this.#qty = n;
    this.#syncFromQty();
  }

  /** Read the product page's current variant + quantity and adopt them. */
  #syncFromProductPage() {
    if (!this.#section) return;
    const idInput = this.#section.querySelector('product-form-component input[name="id"]');
    if (idInput instanceof HTMLInputElement && idInput.value) {
      this.#selectVariantById(idInput.value);
    }
    const qtyInput = this.#section.querySelector(
      'quantity-selector-component input[ref="quantityInput"]'
    );
    if (qtyInput instanceof HTMLInputElement && qtyInput.value) {
      this.#syncQtyFromExternal(qtyInput.value);
    }
  }

  #handlePageVariantUpdate = (event) => {
    const id = event.detail?.resource?.id;
    if (id != null) this.#selectVariantById(id);
  };

  #handlePageQtyUpdate = (event) => {
    // Ignore cart-line quantity selectors; only follow the PDP product stepper.
    if (event.detail?.cartLine) return;
    this.#syncQtyFromExternal(event.detail?.quantity);
  };

  /**
   * Find the variant matching the current option selection. Prefers an exact
   * (and available) match; otherwise holds the axis the customer just changed
   * fixed (so switching language actually moves to that language rather than
   * snapping back to keep the format) and maximizes the other options,
   * preferring available variants — so a combo that doesn't exist snaps to the
   * closest one that does.
   * @param {number} [changedIndex] - Option index the customer just changed.
   */
  #resolveVariant(changedIndex = this.#formatOptionIndex) {
    const sel = this.#selectedOptions;
    const fi = this.#formatOptionIndex;

    if (fi < 0) {
      const fmt = sel[0];
      return (
        this.#variants.find((v) => v.format === fmt && v.available) ??
        this.#variants.find((v) => v.format === fmt) ??
        this.#variant
      );
    }

    const exactAvailable = this.#variants.find(
      (v) => v.available && this.#optionsEqual(v.options, sel)
    );
    if (exactAvailable) return exactAvailable;
    const exact = this.#variants.find((v) => this.#optionsEqual(v.options, sel));
    if (exact) return exact;

    // No exact variant: pivot on the axis the customer just touched (fall back
    // to the format axis) so that option's new value is honored, then pick the
    // closest real variant for the rest.
    const pivot = Number.isFinite(changedIndex) && changedIndex >= 0 ? changedIndex : fi;
    let candidates = this.#variants.filter((v) => v.options[pivot] === sel[pivot]);
    if (!candidates.length) candidates = this.#variants.filter((v) => v.options[fi] === sel[fi]);
    if (!candidates.length) return this.#variant;
    candidates.sort((a, b) => this.#variantScore(b, sel) - this.#variantScore(a, sel));
    return candidates[0];
  }

  #optionsEqual(options, sel) {
    return options.length === sel.length && options.every((opt, i) => opt === sel[i]);
  }

  #variantScore(variant, sel) {
    let score = variant.available ? 1000 : 0;
    variant.options.forEach((opt, i) => {
      if (opt === sel[i]) score += 1;
    });
    return score;
  }

  /** Reflect the current selection back onto the format radios + option selects. */
  #syncControls() {
    const sel = this.#selectedOptions;
    const formatValue =
      this.#formatOptionIndex >= 0 ? sel[this.#formatOptionIndex] : this.#variant?.format;
    for (const input of this.querySelectorAll('[data-format-input]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === formatValue;
    }
    for (const select of this.#optionSelects) {
      const index = Number(select.dataset.optionIndex);
      if (Number.isFinite(index) && sel[index] != null) select.value = sel[index];
    }
  }

  /**
   * Show only the formats that have a variant for the currently-selected
   * non-format options (e.g. language), hiding combinations that don't exist,
   * and annotate out-of-stock formats/values. Mirrors the main variant picker:
   * the format set narrows to the chosen language while every language stays
   * selectable. The selected format is never hidden.
   */
  #updateVariantStates() {
    const sel = this.#selectedOptions;
    const fi = this.#formatOptionIndex >= 0 ? this.#formatOptionIndex : 0;
    const oosLabel = this.dataset.oosLabel || '';

    // Format buttons: keep only formats that exist for the current selection.
    for (const label of this.querySelectorAll('.buy-in-bulk-modal__formats .variant-option__button-label')) {
      const input = label.querySelector('[data-format-input]');
      if (!(input instanceof HTMLInputElement)) continue;

      const matches = this.#variants.filter(
        (v) => v.options[fi] === input.value && this.#matchesExcept(v, sel, fi)
      );
      const exists = matches.length > 0;
      label.classList.toggle('buy-in-bulk-modal__format--hidden', !exists && !input.checked);
      if (!exists) continue;

      // Formats with no bulk schedule are display-only (disabled) and keep
      // their static "No bulk prices" badge — don't overwrite their price or
      // add a second (out-of-stock) badge on top.
      if (input.disabled) continue;

      // Reflect this format's price + stock for the current selection.
      const best = matches.find((v) => v.available) ?? matches[0];
      const priceEl = label.querySelector('[data-format-price]');
      if (priceEl) priceEl.textContent = formatMoney(best.price, this.#moneyFormat, this.#currency);
      this.#toggleStockBadge(label, !best.available, oosLabel);
    }

    // Non-format dropdowns (e.g. Language): every value stays selectable; flag
    // values whose variant for the current selection is out of stock.
    for (const select of this.#optionSelects) {
      const index = Number(select.dataset.optionIndex);
      if (!Number.isFinite(index)) continue;
      for (const option of select.options) {
        const matches = this.#variants.filter(
          (v) => v.options[index] === option.value && this.#matchesExcept(v, sel, index)
        );
        const oos = matches.length > 0 && !matches.some((v) => v.available);
        option.textContent = oos && oosLabel ? `${option.value} - ${oosLabel}` : option.value;
      }
    }
  }

  /** True if `variant` matches `sel` on every option except `exceptIndex`. */
  #matchesExcept(variant, sel, exceptIndex) {
    return variant.options.every((opt, i) => i === exceptIndex || opt === sel[i]);
  }

  /** Add/remove an "out of stock" badge on a format button. */
  #toggleStockBadge(label, oos, oosLabel) {
    label.classList.toggle('buy-in-bulk-modal__format--oos', oos);
    let badge = label.querySelector('[data-format-stock]');
    if (oos) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'variant-option__button-label__stock';
        badge.setAttribute('data-format-stock', '');
        label.appendChild(badge);
      }
      badge.textContent = oosLabel;
    } else if (badge) {
      badge.remove();
    }
  }

  #handleTierChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.tierInput == null) return;
    const index = Number(input.value);
    if (!Number.isFinite(index) || !this.#tiers[index]) return;

    const tier = this.#tiers[index];
    if (this.#qty >= tier.min && (tier.max == null || this.#qty <= tier.max)) {
      this.#syncFromQty();
      return;
    }
    // Moving up → snap to min; moving down → snap to max.
    this.#qty = index < this.#activeTierIndex && tier.max != null ? tier.max : tier.min;
    this.#syncFromQty();
  };

  #handleDecrement = () => {
    this.#qty = Math.max(1, this.#qty - 1);
    this.#syncFromQty();
  };

  #handleIncrement = () => {
    this.#qty = this.#qty + 1;
    this.#syncFromQty();
  };

  #handleQtyInput = () => {
    const parsed = parseInt(this.#qtyInput?.value ?? '', 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      this.#qty = parsed;
      this.#syncFromQty();
    }
  };

  #handleQtyBlur = () => {
    this.#detachKeyboardInset();
    const parsed = parseInt(this.#qtyInput?.value ?? '', 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      this.#qty = 1;
      this.#syncFromQty();
    }
  };

  // The quantity input lives in the drawer's fixed footer, so it can't scroll
  // above the mobile keyboard — instead we shrink/lift the whole drawer to sit
  // above it. `--keyboard-inset` is read by the dialog's CSS (see
  // buy-in-bulk-modal.css). Only relevant on the mobile bottom-drawer layout.
  /** @type {ReturnType<typeof setTimeout>[]} */
  #keyboardInsetTimers = [];

  #handleQtyFocus = () => {
    if (!this.#mobileQuery.matches || !window.visualViewport) return;
    window.visualViewport.addEventListener('resize', this.#applyKeyboardInset);
    window.visualViewport.addEventListener('scroll', this.#applyKeyboardInset);
    // The keyboard animates in; apply once it has settled. The timers are
    // tracked so blur can cancel them — an uncancelled late timer used to
    // re-apply the inset AFTER the blur cleanup ran, leaving a stale inline
    // --keyboard-inset on the dialog that lifted the next open off screen.
    this.#keyboardInsetTimers = [
      setTimeout(this.#applyKeyboardInset, 100),
      setTimeout(this.#applyKeyboardInset, 350),
    ];
  };

  #applyKeyboardInset = () => {
    const viewport = window.visualViewport;
    const dialog = this.refs.dialog;
    if (!viewport || !dialog) return;
    // Pinch/accessibility zoom shrinks visualViewport.height with no keyboard
    // involved, so the layout-viewport math below reads zoom as a huge inset
    // and parks the drawer at the top of the screen. Zoomed in, skip the lift
    // entirely (keyboard-over-drawer beats an invisible drawer).
    if (viewport.scale > 1.05) {
      dialog.style.removeProperty('--keyboard-inset');
      return;
    }
    const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    dialog.style.setProperty('--keyboard-inset', `${Math.round(inset)}px`);
  };

  #detachKeyboardInset = () => {
    for (const timer of this.#keyboardInsetTimers) clearTimeout(timer);
    this.#keyboardInsetTimers = [];
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.#applyKeyboardInset);
      window.visualViewport.removeEventListener('scroll', this.#applyKeyboardInset);
    }
    this.refs.dialog?.style.removeProperty('--keyboard-inset');
  };

  #handleAdd = async () => {
    if (this.#isSubmitting || !this.#variant || !this.#variant.available) return;
    this.#isSubmitting = true;
    if (this.#addLabelEl) this.#addLabelEl.textContent = this.#addingLabel;
    this.#addButton?.setAttribute('disabled', '');

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: [{ id: this.#variant.id, quantity: this.#qty }],
      sections: sectionIds.join(','),
    };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (data && data.status) {
        if (this.#liveRegion) this.#liveRegion.textContent = data.message || '';
        window.publishStorefrontEvent?.('add_to_cart_failed', {
          component: 'buy_in_bulk_modal',
          variant_id: this.#variant ? String(this.#variant.id) : null,
          quantity: this.#qty,
          operation: 'add',
          error_type: 'server_rejected',
          response_status: data.status || null,
          placement: 'buy_in_bulk',
        });
        return;
      }

      document.dispatchEvent(
        new CartAddEvent({}, this.id || 'buy-in-bulk-modal', {
          source: 'buy-in-bulk-modal',
          itemCount: this.#qty,
          variantId: this.#variant.id,
          sections: data?.sections,
        })
      );

      this.closeDialog();
    } catch (error) {
      console.error(error);
      window.publishStorefrontEvent?.('add_to_cart_failed', {
        component: 'buy_in_bulk_modal',
        variant_id: this.#variant ? String(this.#variant.id) : null,
        quantity: this.#qty,
        operation: 'add',
        error_type: 'network',
        placement: 'buy_in_bulk',
      });
    } finally {
      this.#isSubmitting = false;
      this.#updateAddButtonState();
    }
  };
}

/**
 * Strict parser for `custom.bulk_inline_tiers`, ported from the Creatine
 * Function so the modal resolves inline overrides identically. Returns a tier
 * array sorted by descending min_qty, or null on ANY parse failure (matching
 * the Function's explicit-skip behavior: a malformed string = no discount).
 *
 * Accepted per tier (comma-separated): "<min>+ = <pct>%" or "<min>+ = $<price>".
 * @param {string} raw
 * @returns {BulkTier[] | null}
 */
function parseInlineTiers(raw) {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  const tierRe = /^(\d+)\+\s*=\s*(\$[\d.]+|[\d.]+%)$/;
  const segments = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  if (segments.length === 0) return null;

  const tiers = [];
  for (const segment of segments) {
    const match = tierRe.exec(segment);
    if (!match) return null;

    const minQty = parseInt(match[1], 10);
    if (!Number.isFinite(minQty) || minQty <= 0) return null;

    const valueStr = match[2];
    if (valueStr.startsWith('$')) {
      const v = parseFloat(valueStr.slice(1));
      if (!Number.isFinite(v) || v < 0) return null;
      tiers.push({ min_qty: minQty, discount_type: 'fixed_price', value: v });
    } else {
      const v = parseFloat(valueStr.slice(0, -1));
      if (!Number.isFinite(v) || v <= 0 || v > 100) return null;
      tiers.push({ min_qty: minQty, discount_type: 'percent', value: v });
    }
  }

  tiers.sort((a, b) => b.min_qty - a.min_qty);
  return tiers.length > 0 ? tiers : null;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(value) {
  return String(value ?? '').replace(/["'<>&]/g, '');
}

if (!customElements.get('buy-in-bulk-modal')) {
  customElements.define('buy-in-bulk-modal', BuyInBulkModal);
}
